<template lang="pug">
.home
  //- ============ HERO ============
  header.hero
    .hero__grid
    .hero__glow.hero__glow--blue
    .hero__glow.hero__glow--red
    .wrap.hero__inner
      .hero__copy
        p.eyebrow
          span.eyebrow__pip
          | Liga Brasileira de Robótica
        h1.hero__title
          | Engenharia que
          |
          span.hl entra em combate
          | .
        p.hero__sub
          | A LBR reúne equipes de todo o país para construir, inspecionar e colocar
          | seus robôs frente a frente, com chave de dupla eliminação, lutas cronometradas
          | e júri técnico.
        .hero__actions
          button.btn.btn--solid(@click="scrollTo('competicoes')")
            span Próximas competições
            FontAwesomeIcon.btn__ic(:icon="['fas','arrow-right']")
          button.btn.btn--ghost(@click="scrollTo('sobre')")
            span Conheça a liga
        ul.hero__stats
          li.hero__stat(v-for="(s, i) in destaques" :key="i")
            strong {{ s.num }}
            span {{ s.label }}

      .hero__stage
        .stage
          .stage__plate.stage__plate--blue
            FontAwesomeIcon.stage__ic(:icon="['fas','robot']")
            span.stage__tag Lado Azul
          .stage__vs
            span VS
          .stage__plate.stage__plate--red
            FontAwesomeIcon.stage__ic(:icon="['fas','robot']")
            span.stage__tag Lado Vermelho
          .stage__floor

    button.hero__scroll(@click="scrollTo('sobre')" aria-label="Rolar para baixo")
      FontAwesomeIcon(:icon="['fas','chevron-down']")

  //- ============ SOBRE ============
  section.sobre#sobre
    .wrap
      .sec-head.reveal
        p.tag Sobre a LBR
        h2.h2 A liga das equipes que constroem para vencer
        p.sec-sub
          | Mais que um campeonato: um ecossistema completo de cadastro de robôs,
          | inspeção de segurança, chaveamento e apuração transparente de combates.
      .sobre__body
        .sobre__lead.reveal
          p
            | A LBR nasceu dos competidores para ajudar os competidores. A plataforma
            | organiza cada etapa do evento, do cadastro da equipe ao apito final da luta,
            | com regras claras, segurança e resultados transparentes.
          p
            | Cada robô passa por inspeção dupla antes de entrar na arena. As chaves são
            | disputadas em formato de eliminação dupla, e toda luta é avaliada por um júri
            | técnico que pontua dano e agressividade.
        .sobre__features
          .feat.reveal(v-for="(f, i) in pilares" :key="i")
            .feat__ic
              FontAwesomeIcon(:icon="f.icon")
            h3.feat__title {{ f.titulo }}
            p.feat__text {{ f.texto }}

  //- ============ COMPETIÇÕES ============
  section.eventos#competicoes
    .wrap
      .sec-head.reveal
        p.tag Calendário
        h2.h2 Próximas competições
        p.sec-sub Acompanhe as etapas da temporada e garanta a vaga da sua equipe.
      .eventos__grid(v-if="temCompeticoes")
        article.ecard.reveal(
          v-for="(c, i) in proximasCompeticoes"
          :key="c.id"
          :class="i % 2 === 1 ? 'ecard--red' : 'ecard--blue'"
          @click="irParaEvento(c)"
        )
          .ecard__date
            span.ecard__day {{ formatDay(c.startat) }}
            span.ecard__mon {{ formatMonth(c.startat) }}
          .ecard__main
            span.ecard__status(:class="statusClass(eventoStatus(c))") {{ eventoStatus(c) }}
            h3.ecard__name {{ c.name }}
            p.ecard__loc(v-if="c.address")
              FontAwesomeIcon.ecard__loc-ic(:icon="['fas','map-marker-alt']")
              | {{ c.address }}
            span.ecard__link
              | Ver evento
              FontAwesomeIcon.ecard__link-ic(:icon="['fas','arrow-right']")
      .eventos__empty.reveal(v-else)
        FontAwesomeIcon.eventos__empty-ic(:icon="['far','calendar']")
        p Nenhuma competição agendada no momento.
        button.btn.btn--solid(@click="doNavigate('Eventos')")
          span Ver eventos
      p.eventos__note(v-if="temCompeticoes") Clique em uma competição para abrir o resumo do evento.

  //- ============ INFORMAÇÕES ÚTEIS ============
  section.info#info
    .wrap
      .sec-head.reveal
        p.tag Informações úteis
        h2.h2 Tudo o que você precisa
        p.sec-sub Atalhos para acompanhar o evento e dar os primeiros passos na liga.
      .info__grid
        button.icard.reveal(v-for="(card, i) in infoUteis" :key="i" @click="doInfoAction(card)")
          .icard__ic
            FontAwesomeIcon(:icon="card.icon")
          h3.icard__title {{ card.titulo }}
          p.icard__text {{ card.texto }}
          span.icard__cta
            | {{ card.cta }}
            FontAwesomeIcon.icard__cta-ic(:icon="['fas','arrow-right']")

  //- ============ CHAMADA FINAL ============
  section.cta
    .wrap.cta__wrap.reveal
      .cta__text
        h2 Pronto para entrar na arena?
        p Construa, inscreva e dispute. Coloque sua equipe na próxima etapa da LBR.
      .cta__actions
        button.btn.btn--solid(@click="mailTo()")
          span Inscrever equipe
          FontAwesomeIcon.btn__ic(:icon="['fas','user-plus']")
        button.btn.btn--ghost.btn--ghost-dark(@click="scrollTo('competicoes')")
          span Ver calendário

  //- ============ RODAPÉ ============
  footer.foot
    .wrap.foot__wrap
      .foot__brand
        img.foot__logo(:src="lbrLogo" alt="LBR")
        .foot__brand-txt
          strong Liga Brasileira de Robótica
          span Robôs de combate · Brasil
      .foot__links
        a(@click="doNavigate('Eventos')") Eventos
        a(@click="doNavigate('Fights')") Lutas
        a(@click="doNavigate('Keys')") Chaves
        a(@click="doNavigate('Robots')") Robôs
        a(href="mailto:ligabrobotica@gmail.com") Contato
    .foot__bar
      span © 2026 Liga Brasileira de Robótica
      span Feito para a comunidade de robótica de combate
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import lbrLogo from '@/assets/lbrLogo.webp'
import { statusOf, eventDay, today } from '@/utils/eventDate'

const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ']

export default {
  name: 'Home',
  data: () => ({
    lbrLogo,
    destaques: [
      { num: 'RED × BLUE', label: 'Formato de combate' },
      { num: 'Chave dupla', label: 'Winner & loser bracket' },
      { num: '3 juízes', label: 'Júri técnico por luta' },
      { num: 'Inspeção 2×', label: 'Estática e dinâmica' }
    ],
    pilares: [
      {
        icon: ['fas', 'users'],
        titulo: 'Equipes & robôs',
        texto: 'Cadastro de equipes e robôs por categoria, com perfil e histórico de cada competidor.'
      },
      {
        icon: ['fas', 'shield-alt'],
        titulo: 'Inspeção de segurança',
        texto: 'Toda máquina passa por inspeção estática e dinâmica antes de ser liberada para a arena.'
      },
      {
        icon: ['fas', 'sitemap'],
        titulo: 'Chaveamento',
        texto: 'Chaves em dupla eliminação, com bracket de vencedores e repescagem dos perdedores.'
      },
      {
        icon: ['fas', 'gavel'],
        titulo: 'Lutas & júri',
        texto: 'Combates cronometrados, pontuados por dano e agressividade e decididos por júri técnico.'
      }
    ],
    infoUteis: [
      {
        icon: ['fas', 'trophy'],
        titulo: 'Resumo do evento',
        texto: 'Categorias, robôs inscritos e números de cada etapa em um só lugar.',
        cta: 'Ver eventos',
        acao: { tipo: 'route', alvo: 'Eventos' }
      },
      {
        icon: ['fas', 'sitemap'],
        titulo: 'Chaves & resultados',
        texto: 'Acompanhe o chaveamento de cada categoria, com vencedores e repescagem.',
        cta: 'Ver chaves',
        acao: { tipo: 'route', alvo: 'Keys' }
      },
      {
        icon: ['fas', 'bolt'],
        titulo: 'Lutas ao vivo',
        texto: 'Programação das lutas, arenas e horários atualizados em tempo real.',
        cta: 'Ver lutas',
        acao: { tipo: 'route', alvo: 'Fights' }
      },
      {
        icon: ['fas', 'robot'],
        titulo: 'Robôs inscritos',
        texto: 'Conheça os competidores e o status das inspeções de cada robô.',
        cta: 'Ver robôs',
        acao: { tipo: 'route', alvo: 'Robots' }
      },
      {
        icon: ['fas', 'user-plus'],
        titulo: 'Inscreva sua equipe',
        texto: 'Cadastre equipe e robôs para disputar a próxima etapa da liga.',
        cta: 'Quero participar',
        acao: { tipo: 'mail', alvo: 'ligabrobotica@gmail.com' }
      },
      {
        icon: ['far', 'question-circle'],
        titulo: 'Fale com a LBR',
        texto: 'Dúvidas sobre eventos, parcerias e imprensa? Estamos por aqui.',
        cta: 'Enviar e-mail',
        acao: { tipo: 'mail', alvo: 'ligabrobotica@gmail.com' }
      }
    ]
  }),
  computed: {
    ...mapGetters('events', ['getEvents']),
    proximasCompeticoes () {
      // dia de calendário, não instante: senão o evento sai da home às 21h do
      // último dia (meia-noite UTC), ainda durante a competição.
      const hoje = today()
      return (this.getEvents || [])
        .filter(e => !e.is_test)
        .filter(e => {
          const fim = eventDay(e.endat)
          return fim === null || fim >= hoje
        })
        .sort((a, b) => new Date(a.startat || 0) - new Date(b.startat || 0))
        .slice(0, 6)
    },
    temCompeticoes () {
      return this.proximasCompeticoes.length > 0
    }
  },
  mounted () {
    if (typeof IntersectionObserver === 'undefined') return
    this._io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in')
          this._io.unobserve(entry.target)
        }
      })
    }, { threshold: 0.12 })
    this.$nextTick(() => {
      this.$el.querySelectorAll('.reveal').forEach(el => this._io.observe(el))
    })
  },
  beforeDestroy () {
    if (this._io) this._io.disconnect()
  },
  methods: {
    ...mapActions('events', ['setCurrentEventId']),
    doNavigate (name) {
      if (name && name !== this.$route.name) {
        this.$router.push(name)
      }
    },
    scrollTo (id) {
      const el = document.getElementById(id)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    mailTo () {
      window.location.href = 'mailto:ligabrobotica@gmail.com'
    },
    doInfoAction (card) {
      if (card.acao.tipo === 'route') {
        this.doNavigate(card.acao.alvo)
      } else if (card.acao.tipo === 'mail') {
        window.location.href = 'mailto:' + card.acao.alvo
      } else {
        window.open(card.acao.alvo, '_blank', 'noopener')
      }
    },
    irParaEvento (e) {
      this.setCurrentEventId(e.id)
      this.$router.push('/EventoDetalhe')
    },
    // dia de calendário gravado como meia-noite UTC: ler em UTC (getters locais
    // devolviam o dia anterior em UTC-3)
    formatDay (value) {
      if (!value) return '-'
      const d = new Date(value)
      return isNaN(d.getTime()) ? '-' : String(d.getUTCDate()).padStart(2, '0')
    },
    formatMonth (value) {
      if (!value) return ''
      const d = new Date(value)
      return isNaN(d.getTime()) ? '' : MESES[d.getUTCMonth()]
    },
    eventoStatus (e) {
      return { done: 'Encerrado', scheduled: 'Agendado' }[statusOf(e)] || 'Em andamento'
    },
    statusClass (status) {
      if (status === 'Em andamento') return 'is-open'
      if (status === 'Agendado') return 'is-soon'
      return 'is-closed'
    }
  }
}
</script>

<style lang="sass" scoped>
$blue: #287AED
$blue-dark: #0F4FA8
$red: #DD3434
$red-dark: #B0231F
$ink: #0B0E14
$ink-2: #141B26
$paper: #FFFFFF
$mist: #EEF1F6
$muted: #5C6673
$line: rgba(11, 14, 20, 0.10)

$display: 'Saira Condensed', 'Lato', sans-serif
$mono: 'Space Mono', 'Lato', monospace

=bleed
  position: relative
  left: 50%
  margin-left: -50vw
  width: 100vw

.home
  overflow-x: hidden
  color: $ink
  font-family: Lato, sans-serif
  line-height: 1.6

  .wrap
    width: 100%
    max-width: 1140px
    margin: 0 auto
    padding: 0 24px
    box-sizing: border-box

  // ---------- shared ----------
  .tag
    font-family: $mono
    text-transform: uppercase
    letter-spacing: 0.22em
    font-size: 12px
    font-weight: 700
    color: $blue
    margin: 0 0 14px

  .h2
    font-family: $display
    font-weight: 700
    font-size: clamp(30px, 4.4vw, 48px)
    line-height: 1.04
    letter-spacing: -0.01em
    margin: 0
    text-transform: uppercase

  .sec-head
    max-width: 720px
    margin-bottom: 48px

  .sec-sub
    font-size: 18px
    color: $muted
    margin: 18px 0 0

  .btn
    display: inline-flex
    align-items: center
    gap: 10px
    border: none
    cursor: pointer
    font-family: $display
    font-weight: 600
    text-transform: uppercase
    letter-spacing: 0.06em
    font-size: 16px
    padding: 0 26px
    height: 52px
    border-radius: 4px
    transition: transform .18s ease, box-shadow .18s ease, background .18s ease
    &__ic
      font-size: 13px
    &--solid
      background: $blue
      color: #fff
      box-shadow: 0 10px 24px rgba(40, 122, 237, 0.35)
      &:hover
        transform: translateY(-2px)
        box-shadow: 0 14px 30px rgba(40, 122, 237, 0.45)
    &--ghost
      background: transparent
      color: #fff
      border: 2px solid rgba(255, 255, 255, 0.35)
      &:hover
        border-color: #fff
        transform: translateY(-2px)
    &--ghost-dark
      color: $ink
      border-color: rgba(11, 14, 20, 0.25)
      &:hover
        border-color: $ink

  // ---------- reveal ----------
  .reveal
    opacity: 0
    transform: translateY(26px)
    transition: opacity .7s cubic-bezier(.2,.7,.2,1), transform .7s cubic-bezier(.2,.7,.2,1)
    &.is-in
      opacity: 1
      transform: none

  // ---------- HERO ----------
  .hero
    +bleed
    background: radial-gradient(circle at 18% 12%, #15233b 0%, $ink 46%, #05070b 100%)
    color: #fff
    padding: 96px 0 88px
    overflow: hidden
    isolation: isolate

    &__grid
      position: absolute
      inset: 0
      background-image: linear-gradient(rgba(255, 255, 255, 0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px)
      background-size: 46px 46px
      mask-image: radial-gradient(circle at 60% 30%, #000 0%, transparent 78%)
      -webkit-mask-image: radial-gradient(circle at 60% 30%, #000 0%, transparent 78%)
      z-index: -2

    &__glow
      position: absolute
      width: 520px
      height: 520px
      border-radius: 50%
      filter: blur(120px)
      opacity: 0.5
      z-index: -1
      &--blue
        background: $blue
        top: -160px
        left: -120px
      &--red
        background: $red
        bottom: -200px
        right: -120px

    &__inner
      display: grid
      grid-template-columns: 1.05fr 0.95fr
      gap: 56px
      align-items: center

    &__copy > *
      animation: rise .7s cubic-bezier(.2,.7,.2,1) both
    &__copy
      .eyebrow
        animation-delay: .05s
      .hero__title
        animation-delay: .14s
      .hero__sub
        animation-delay: .22s
      .hero__actions
        animation-delay: .30s
      .hero__stats
        animation-delay: .38s

    &__title
      font-family: $display
      font-weight: 800
      text-transform: uppercase
      font-size: clamp(46px, 6.6vw, 92px)
      line-height: 0.92
      letter-spacing: -0.015em
      margin: 18px 0 0
      .hl
        background: linear-gradient(100deg, #5AA0FF, $red)
        -webkit-background-clip: text
        background-clip: text
        color: transparent

    &__sub
      max-width: 480px
      font-size: 18px
      color: rgba(255, 255, 255, 0.74)
      margin: 24px 0 0

    &__actions
      display: flex
      flex-wrap: wrap
      gap: 14px
      margin-top: 34px

    &__stats
      list-style: none
      display: grid
      grid-template-columns: repeat(4, auto)
      gap: 30px
      padding: 0
      margin: 52px 0 0
      border-top: 1px solid rgba(255, 255, 255, 0.12)
      padding-top: 26px

    &__stat
      display: flex
      flex-direction: column
      strong
        font-family: $display
        font-weight: 700
        text-transform: uppercase
        font-size: 22px
        letter-spacing: 0.01em
        color: #fff
      span
        font-family: $mono
        font-size: 11px
        letter-spacing: 0.08em
        color: rgba(255, 255, 255, 0.55)
        margin-top: 6px

    &__scroll
      position: absolute
      left: 50%
      bottom: 22px
      transform: translateX(-50%)
      background: none
      border: none
      color: rgba(255, 255, 255, 0.5)
      font-size: 20px
      cursor: pointer
      animation: bob 1.8s ease-in-out infinite
      &:hover
        color: #fff

  // hero stage / VS
  .stage
    position: relative
    display: grid
    grid-template-columns: 1fr auto 1fr
    align-items: center
    gap: 8px
    padding: 30px
    border: 1px solid rgba(255, 255, 255, 0.10)
    border-radius: 16px
    background: linear-gradient(160deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.01))
    backdrop-filter: blur(6px)
    animation: rise .8s cubic-bezier(.2,.7,.2,1) .28s both

    &__plate
      display: flex
      flex-direction: column
      align-items: center
      gap: 14px
      padding: 30px 12px
      border-radius: 12px
      transition: transform .3s ease
      &--blue
        background: radial-gradient(circle at 50% 0%, rgba(40, 122, 237, 0.30), transparent 70%)
        box-shadow: inset 0 0 0 1px rgba(90, 160, 255, 0.35)
        .stage__ic
          color: #6FB0FF
        &:hover
          transform: translateY(-6px)
      &--red
        background: radial-gradient(circle at 50% 0%, rgba(221, 52, 52, 0.30), transparent 70%)
        box-shadow: inset 0 0 0 1px rgba(255, 110, 110, 0.35)
        .stage__ic
          color: #FF7A7A
        &:hover
          transform: translateY(-6px)

    &__ic
      font-size: 64px

    &__tag
      font-family: $mono
      font-size: 11px
      letter-spacing: 0.16em
      text-transform: uppercase
      color: rgba(255, 255, 255, 0.7)

    &__vs
      span
        display: grid
        place-items: center
        width: 56px
        height: 56px
        border-radius: 50%
        font-family: $display
        font-weight: 800
        font-size: 22px
        color: #fff
        background: $ink
        box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.18), 0 8px 24px rgba(0, 0, 0, 0.5)

    &__floor
      position: absolute
      left: 24px
      right: 24px
      bottom: 14px
      height: 1px
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.25), transparent)

  // ---------- SOBRE ----------
  .sobre
    padding: 96px 0
    .sobre__body
      display: grid
      grid-template-columns: 1fr 1fr
      gap: 56px
      align-items: start
    .sobre__lead
      p
        font-size: 18px
        color: lighten($ink, 14%)
        margin: 0 0 20px
      p:first-child
        font-size: 20px
        color: $ink
        font-weight: 400
        border-left: 3px solid $red
        padding-left: 20px

    .sobre__features
      display: grid
      grid-template-columns: 1fr 1fr
      gap: 18px
      &.reveal
        transition-delay: .08s

    .feat
      padding: 26px 22px
      border: 1px solid $line
      border-radius: 12px
      background: $paper
      transition: transform .25s ease, box-shadow .25s ease, border-color .25s ease
      &:hover
        transform: translateY(-4px)
        box-shadow: 0 16px 36px rgba(11, 14, 20, 0.08)
        border-color: transparent
      &:nth-child(2)
        transition-delay: .06s
      &:nth-child(3)
        transition-delay: .12s
      &:nth-child(4)
        transition-delay: .18s
      &__ic
        display: grid
        place-items: center
        width: 48px
        height: 48px
        border-radius: 10px
        font-size: 20px
        color: #fff
        background: linear-gradient(140deg, $blue, $blue-dark)
        margin-bottom: 16px
      &:nth-child(even)
        .feat__ic
          background: linear-gradient(140deg, $red, $red-dark)
      &__title
        font-family: $display
        text-transform: uppercase
        font-size: 20px
        font-weight: 700
        margin: 0 0 8px
      &__text
        font-size: 15px
        color: $muted
        margin: 0

  // ---------- COMPETIÇÕES ----------
  .eventos
    +bleed
    background: $mist
    padding: 96px 0
    .eventos__grid
      display: grid
      grid-template-columns: repeat(auto-fill, minmax(320px, 1fr))
      gap: 22px
    .eventos__note
      font-family: $mono
      font-size: 12px
      letter-spacing: 0.04em
      color: $muted
      margin: 28px 0 0
      text-align: center
    .eventos__empty
      display: flex
      flex-direction: column
      align-items: center
      text-align: center
      gap: 16px
      padding: 56px 24px
      background: $paper
      border: 1px dashed rgba(11, 14, 20, 0.18)
      border-radius: 16px
      &-ic
        font-size: 40px
        color: $blue
      p
        font-size: 18px
        color: $muted
        margin: 0

  .ecard
    display: flex
    gap: 20px
    padding: 24px
    background: $paper
    border-radius: 14px
    border: 1px solid $line
    border-top: 4px solid $blue
    cursor: pointer
    transition: transform .25s ease, box-shadow .25s ease
    &--red
      border-top-color: $red
      .ecard__date
        background: linear-gradient(150deg, $red, $red-dark)
    &--blue
      .ecard__date
        background: linear-gradient(150deg, $blue, $blue-dark)
    &:hover
      transform: translateY(-5px)
      box-shadow: 0 20px 44px rgba(11, 14, 20, 0.12)
      .ecard__link-ic
        transform: translateX(5px)
    &:nth-child(2)
      transition-delay: .06s
    &:nth-child(3)
      transition-delay: .12s
    &:nth-child(4)
      transition-delay: .18s

    &__date
      flex-shrink: 0
      display: flex
      flex-direction: column
      align-items: center
      justify-content: center
      width: 72px
      height: 72px
      border-radius: 12px
      background: $ink
      color: #fff
    &__day
      font-family: $display
      font-weight: 800
      font-size: 30px
      line-height: 1
    &__mon
      font-family: $mono
      font-size: 11px
      letter-spacing: 0.14em
      margin-top: 4px
      opacity: 0.85

    &__main
      min-width: 0
    &__status
      display: inline-block
      font-family: $mono
      font-size: 10px
      font-weight: 700
      letter-spacing: 0.1em
      text-transform: uppercase
      padding: 4px 10px
      border-radius: 999px
      margin-bottom: 10px
      &.is-open
        background: rgba(34, 160, 80, 0.12)
        color: #1F8B45
      &.is-soon
        background: rgba(40, 122, 237, 0.12)
        color: $blue-dark
      &.is-closed
        background: rgba(92, 102, 115, 0.14)
        color: $muted
    &__name
      font-family: $display
      text-transform: uppercase
      font-size: 23px
      font-weight: 700
      line-height: 1.05
      margin: 0 0 8px
    &__loc
      display: flex
      align-items: center
      gap: 8px
      font-size: 14px
      color: $muted
      margin: 0 0 14px
      &-ic
        color: $red
        font-size: 13px
    &__link
      display: inline-flex
      align-items: center
      gap: 8px
      font-family: $mono
      font-size: 12px
      font-weight: 700
      letter-spacing: 0.06em
      text-transform: uppercase
      color: $blue
      &-ic
        font-size: 11px
        transition: transform .25s ease

  // ---------- INFO ----------
  .info
    padding: 96px 0
    .info__grid
      display: grid
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr))
      gap: 18px
    .icard
      text-align: left
      cursor: pointer
      font-family: inherit
      padding: 28px 24px
      border-radius: 14px
      border: 1px solid $line
      background: $paper
      transition: transform .25s ease, box-shadow .25s ease, border-color .25s ease
      &:hover
        transform: translateY(-5px)
        box-shadow: 0 20px 44px rgba(11, 14, 20, 0.10)
        border-color: rgba(40, 122, 237, 0.4)
        .icard__cta-ic
          transform: translateX(5px)
      &:nth-child(3n+2)
        transition-delay: .06s
      &:nth-child(3n+3)
        transition-delay: .12s
      &__ic
        display: grid
        place-items: center
        width: 52px
        height: 52px
        border-radius: 12px
        font-size: 22px
        color: $blue
        background: rgba(40, 122, 237, 0.10)
        margin-bottom: 18px
      &:nth-child(even)
        .icard__ic
          color: $red
          background: rgba(221, 52, 52, 0.10)
      &__title
        font-family: $display
        text-transform: uppercase
        font-size: 21px
        font-weight: 700
        margin: 0 0 8px
      &__text
        font-size: 15px
        color: $muted
        margin: 0 0 18px
      &__cta
        display: inline-flex
        align-items: center
        gap: 8px
        font-family: $mono
        font-size: 12px
        font-weight: 700
        letter-spacing: 0.06em
        text-transform: uppercase
        color: $blue
        &-ic
          font-size: 11px
          transition: transform .25s ease

  // ---------- CTA ----------
  .cta
    +bleed
    background: radial-gradient(circle at 80% 20%, #15233b, $ink 60%)
    color: #fff
    padding: 80px 0
    .cta__wrap
      display: flex
      flex-wrap: wrap
      align-items: center
      justify-content: space-between
      gap: 32px
    .cta__text
      h2
        font-family: $display
        text-transform: uppercase
        font-weight: 800
        font-size: clamp(28px, 4vw, 44px)
        line-height: 1
        margin: 0
      p
        font-size: 18px
        color: rgba(255, 255, 255, 0.72)
        margin: 14px 0 0
        max-width: 440px
    .cta__actions
      display: flex
      flex-wrap: wrap
      gap: 14px
      .btn--ghost-dark
        color: #fff
        border-color: rgba(255, 255, 255, 0.35)
        &:hover
          border-color: #fff

  // ---------- FOOT ----------
  .foot
    +bleed
    background: #05070b
    color: rgba(255, 255, 255, 0.75)
    padding: 54px 0 0
    .foot__wrap
      display: flex
      flex-wrap: wrap
      align-items: center
      justify-content: space-between
      gap: 26px
      padding-bottom: 40px
    .foot__brand
      display: flex
      align-items: center
      gap: 14px
      &-txt
        display: flex
        flex-direction: column
        strong
          font-family: $display
          text-transform: uppercase
          letter-spacing: 0.02em
          color: #fff
          font-size: 17px
        span
          font-family: $mono
          font-size: 11px
          letter-spacing: 0.06em
          color: rgba(255, 255, 255, 0.5)
    .foot__logo
      width: 44px
      height: 44px
      object-fit: contain
    .foot__links
      display: flex
      flex-wrap: wrap
      gap: 22px
      a
        color: rgba(255, 255, 255, 0.72)
        text-decoration: none
        font-size: 15px
        cursor: pointer
        transition: color .2s ease
        &:hover
          color: #fff
    .foot__bar
      border-top: 1px solid rgba(255, 255, 255, 0.08)
      padding: 20px 24px
      max-width: 1140px
      margin: 0 auto
      display: flex
      flex-wrap: wrap
      justify-content: space-between
      gap: 8px
      font-family: $mono
      font-size: 11px
      letter-spacing: 0.04em
      color: rgba(255, 255, 255, 0.4)

  // ---------- keyframes ----------
  @keyframes rise
    from
      opacity: 0
      transform: translateY(20px)
    to
      opacity: 1
      transform: none

  @keyframes bob
    0%, 100%
      transform: translate(-50%, 0)
    50%
      transform: translate(-50%, 7px)

  // ---------- responsive ----------
  @media (max-width: 920px)
    .hero
      padding: 72px 0 80px
      &__inner
        grid-template-columns: 1fr
        gap: 44px
      &__stats
        grid-template-columns: repeat(2, 1fr)
        gap: 22px
    .sobre__body
      grid-template-columns: 1fr
      gap: 36px

  @media (max-width: 560px)
    .home .wrap
      padding: 0 18px
    .hero__stats
      grid-template-columns: 1fr 1fr
    .sobre__features
      grid-template-columns: 1fr
    .stage
      grid-template-columns: 1fr auto 1fr
      padding: 20px
    .stage__ic
      font-size: 44px
    .ecard
      flex-direction: column
      gap: 16px
    .cta__wrap
      flex-direction: column
      align-items: flex-start
    .foot__wrap
      flex-direction: column
      align-items: flex-start
</style>
